import React from "react";
import { useSelector } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../../ui-components/table/table";
import CustomCell, {
  CustomCellTypes
} from "../../../../ui-components/table/custom-cells/customCell";
import BlockPlaceholder from "../../../../ui-components/block-placeholder/blockPlaceholder";
import { InvoiceDetailsItem } from "../../../../app/data/invoice/models";
import {
  ContentItem,
  ContentItemTitle
} from "../../../../templates/content-item/contentItem";
import XGSNavLink from "../../../../ui-components/xgs-nav-link/xgsNavLink";
import { userSelector } from "../../../../slices/user/userSlice";
import UserState from "../../../../slices/user/UserState";
import { UserUtils } from "../../../../app/data/user/userUtils";

export interface InvoiceDetailsTableProps {
  items?: InvoiceDetailsItem[];
  invoiceAmount?: number;
}

const parseProbills = (descriptionItem: string) => {
  if (!descriptionItem) return null;
  const probillRegex = /(\b\d{6,8}\b(\b|$))/gi;
  let descriptionItemBlocks = descriptionItem.split(probillRegex);
  let result = descriptionItemBlocks.map(block => {
    if (block.match(probillRegex)) {
      return <XGSNavLink
        to={"/shipments/" + block}
        target="_blank"
        className="xgs-invoice-details__table__link"
        rel="noopener noreferrer">
          {block}
        </XGSNavLink>;
    } else {
      return block;
    }
  });
  return result;
};

const columnHelper = createColumnHelper<any>();

const InvoiceDetailsTable: React.FC<InvoiceDetailsTableProps> = (props) => {
  const userState: UserState = useSelector(userSelector);

  const columns = [
    columnHelper.accessor("pieces", {
      id: "pieces",
      header: () => (
        <div style={{ textAlign: "right" }}>
          Pieces
        </div>
      ),
      cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
      size: 60,
    }),
    columnHelper.accessor("commodityCode", {
      id: "commodityCode",
      header: () => (
        <div style={{ textAlign: "center" }}>
          CC
        </div>
      ),
      cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
      size: 50,
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: "Description",
      cell: (cellProps) => parseProbills(cellProps.getValue()),
      size: 308,
    }),
    columnHelper.accessor("units", {
      id: "units",
      header: "Units",
      size: 60
    }),
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: () => (
        <div style={{ textAlign: "right" }}>
          <span title="Quantity">Qty</span>
        </div>
      ),
      cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
      size: 100
    }),
    columnHelper.accessor("weight", {
      id: "weight",
      header: () => (
        <div style={{ textAlign: "right" }}>
          Weight
        </div>
      ),
      cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
      size: 100
    }),
    columnHelper.accessor("rate", {
      id: "rate",
      header: () => (
        <div style={{ textAlign: "right" }}>
          {UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") && (
            <>
              Rate
            </>
          )}          
        </div>
      ),
      cell: (cellProps) => (
        <div style={{ textAlign: "right" }}>
          {UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") && (
            <>
              {cellProps.getValue()}
            </>
          )}
        </div>
      ),
      size: UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") ? 100 : 5
    }),
    columnHelper.accessor("freightCharges", {
      id: "freightCharges",
      header: () => (
        <div style={{ textAlign: "right" }}>
          {UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") && (
            <>
              Freight charges
            </>
          )}
        </div>
      ),
      cell: (cellProps) => (
        <div style={{ textAlign: "right" }}>
          {UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") && (
            <CustomCell
              cellType={CustomCellTypes.amount}
              selector="freightCharges"
              data={cellProps.row.original}
              value={cellProps.getValue() || "0"}
            />
          )}
        </div>
      ),
      size: UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") ? 100 : 5
    }),
  ];

  return (
    <ContentItem className="xgs-invoice-details__table">
      <ContentItemTitle>Invoice Details</ContentItemTitle>
      {props.items && (
        <Table
          columns={columns}
          data={props.items}
          totals={UserUtils.hasPermission(userState.profile || null, userState.activeSubAccount?.id || null, "PAYMENT") ? [
            {
              title: "Total Charges",
              type: CustomCellTypes.amount,
              value: props.invoiceAmount || "0"
            },
          ] : undefined}
        />
      )}
      {!props.items && <BlockPlaceholder />}
    </ContentItem>
  );
};

export default InvoiceDetailsTable;
